import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Buffer } from "buffer";
import PlanActions from "../Redux/transition/plan/plan.reducer";
import PlanGroupActions from "../Redux/transition/plan-group/plan-group.reducer";
import "../Styles/components/Share.scss";

const QR_DOWNLOAD_SIZE = 500;

const downloadResizedQrPng = (arrayBuffer, fileName, size = QR_DOWNLOAD_SIZE) => {
  const blob = new Blob([arrayBuffer], { type: "image/png" });
  const objectUrl = URL.createObjectURL(blob);
  const image = new Image();

  image.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");
    context.imageSmoothingEnabled = false;
    context.drawImage(image, 0, 0, size, size);

    canvas.toBlob((resizedBlob) => {
      URL.revokeObjectURL(objectUrl);
      if (!resizedBlob) return;

      const downloadUrl = URL.createObjectURL(resizedBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    }, "image/png");
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    const fallbackUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = fallbackUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(fallbackUrl);
  };

  image.src = objectUrl;
};

const ShareQrPanel = ({ visible, entityType, entityId, fileName = "qr-code.png" }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [downloaded, setDownloaded] = useState(false);

  const isPlan = entityType === "plan";

  const fetchingQrCode = useSelector((state) =>
    isPlan ? state.plans.fetchingQrCode : state.planGroups.fetchingQrCode
  );
  const qrCode = useSelector((state) =>
    isPlan ? state.plans.qrCode : state.planGroups.qrCode
  );

  const fetchQrCode = useCallback(() => {
    if (!entityId) return;
    if (isPlan) {
      dispatch(PlanActions.planQrCodeRequest(entityId));
    } else {
      dispatch(PlanGroupActions.planGroupQrCodeRequest(entityId));
    }
  }, [dispatch, entityId, isPlan]);

  useEffect(() => {
    if (visible && entityId) {
      fetchQrCode();
    }
    if (!visible) {
      setDownloaded(false);
    }
  }, [visible, entityId, fetchQrCode]);

  useEffect(() => {
    if (qrCode instanceof ArrayBuffer) {
      setQrCodeUrl(Buffer.from(qrCode).toString("base64"));
    } else {
      setQrCodeUrl("");
    }
  }, [qrCode]);

  const handleDownload = () => {
    if (!(qrCode instanceof ArrayBuffer)) return;
    downloadResizedQrPng(qrCode, fileName);
    setDownloaded(true);
  };

  if (!entityId) {
    return null;
  }

  return (
    <div className="share-qr-panel">
      {fetchingQrCode ? (
        <div className="share-qr-frame share-qr-loading">
          <ProgressSpinner style={{ width: "4rem", height: "4rem" }} strokeWidth="4" />
        </div>
      ) : qrCodeUrl ? (
        <>
          <div className="share-qr-frame">
            <img
              src={`data:image/png;base64,${qrCodeUrl}`}
              alt={t("share.qr_code")}
              className="share-qr-image"
            />
          </div>
          <Button
            label={downloaded ? t("share.downloaded") : t("share.download")}
            icon={downloaded ? "pi pi-check" : "pi pi-download"}
            className="share-qr-download-btn"
            id="download_QR"
            onClick={handleDownload}
            disabled={fetchingQrCode}
            loading={fetchingQrCode}
          />
        </>
      ) : null}
    </div>
  );
};

export default ShareQrPanel;
